import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Divider,
    Button,
    Link,
} from '@mui/material';
import CONFIG from '../../api/config';

interface CancelSubscriptionDialogProps {
    isOpen: boolean;
    subscriptionName: string;
    onClose: () => void;
    onConfirmCancel: () => void;
}

export default function CancelSubscriptionDialog({
    isOpen,
    subscriptionName,
    onClose,
    onConfirmCancel,
}: CancelSubscriptionDialogProps) {
    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            data-testid="cancel-subscription-dialog"
        >
            <DialogTitle sx={{ pb: 0 }}>
                <Typography variant="h6" component="span" fontWeight={700}>
                    We&apos;d hate to see you go!
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    Cancelling means losing access to your coach&apos;s expert programming 
                    and your <strong>{subscriptionName}</strong> community. If there&apos;s 
                    something we can improve, we&apos;d love to hear about it. 
                    Try reaching out to your coach or{' '}
                    <Link href={CONFIG.URL_SUPPORT}>contact support</Link> before 
                    you go.
                </Typography>
                <Divider />
                <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        If you cancel, you&apos;ll lose access to:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Expert coaching and intentional programming
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Your team community and accountability
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Custom exercise videos and cues from your coach
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onClose}
                    fullWidth
                >
                    Keep My Subscription
                </Button>
                <Button
                    variant="text"
                    onClick={onConfirmCancel}
                    fullWidth
                    sx={{ color: 'error.main' }}
                >
                    Cancel Subscription
                </Button>
            </DialogActions>
        </Dialog>
    );
}
